/**
 * Brand Plane Bridge — BrandIdentity reduce · accentOn · accent clamp
 * @see docs/inflomatrix-constitution/ui-architecture/BRAND_PLANE_BRIDGE.md §12-A · §12-B
 */

import type { SiteDesignManifest } from '@shared/site/types/site-design-manifest';
import type { TenantSiteModel } from '@shared/site/types/tenant-site-model';
import { themeFontsFromManifest } from '@shared/site/design/generate-design-css-vars';
import { resolveSiteBrandLabel } from '@shared/site/site-brand-label';

export interface BrandIdentity {
  accent: string;
  accentOn: string;
  logoUrl?: string;
  tenantDisplayName?: string;
  fontBody?: string;
  radiusPx?: number;
  injectionLevel: 'L1' | 'L2';
  accentWasClamped?: boolean;
}

export type TenantSiteThemeSlice = TenantSiteModel['theme'];

/** Business dark surfaces — WCAG clamp reference (§7 · §12-B) */
export const BUSINESS_PLANE_CONTRAST_SURFACES = [
  '#06081a',
  '#0d1235',
  '#0a0f2b',
  'rgba(255, 255, 255, 0.03)',
  'rgba(255, 255, 255, 0.04)',
  'rgba(255, 255, 255, 0.06)',
] as const;

const ACCENT_ON_CANDIDATES = ['#ffffff', '#000000', '#0f172a', '#06081a'] as const;
const WCAG_AA_CONTRAST = 4.5;
const L2_RADIUS_MIN = 8;
const L2_RADIUS_MAX = 16;

interface Rgb {
  r: number;
  g: number;
  b: number;
  a: number;
}

function clampByte(value: number): number {
  return Math.min(255, Math.max(0, Math.round(value)));
}

function expandHex(hex: string): string | null {
  const normalized = hex.trim().toLowerCase();
  if (/^#[0-9a-f]{3}$/.test(normalized)) {
    const [, r, g, b] = normalized;
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  if (/^#[0-9a-f]{6}$/.test(normalized)) {
    return normalized;
  }
  return null;
}

export function parseCssColor(input: string): Rgb | null {
  const trimmed = input.trim();
  const hex = expandHex(trimmed);
  if (hex) {
    const r = Number.parseInt(hex.slice(1, 3), 16);
    const g = Number.parseInt(hex.slice(3, 5), 16);
    const b = Number.parseInt(hex.slice(5, 7), 16);
    return { r, g, b, a: 1 };
  }

  const rgbaMatch =
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/i.exec(trimmed);
  if (rgbaMatch) {
    const r = clampByte(Number(rgbaMatch[1]));
    const g = clampByte(Number(rgbaMatch[2]));
    const b = clampByte(Number(rgbaMatch[3]));
    const a = rgbaMatch[4] !== undefined ? Number(rgbaMatch[4]) : 1;
    return { r, g, b, a: Number.isFinite(a) ? a : 1 };
  }

  return null;
}

function blendOverBackground(fg: Rgb, bg: Rgb): Rgb {
  const alpha = fg.a + bg.a * (1 - fg.a);
  if (alpha <= 0) {
    return { r: 0, g: 0, b: 0, a: 0 };
  }
  return {
    r: (fg.r * fg.a + bg.r * bg.a * (1 - fg.a)) / alpha,
    g: (fg.g * fg.a + bg.g * bg.a * (1 - fg.a)) / alpha,
    b: (fg.b * fg.a + bg.b * bg.a * (1 - fg.a)) / alpha,
    a: alpha,
  };
}

function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (value: number) => {
    const srgb = value / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function contrastRatio(foreground: string, background: string): number {
  const fg = parseCssColor(foreground);
  const bg = parseCssColor(background);
  if (!fg || !bg) {
    return 1;
  }

  const layoutBg = parseCssColor('#06081a') ?? { r: 6, g: 8, b: 26, a: 1 };
  const resolvedBg = bg.a < 1 ? blendOverBackground(bg, layoutBg) : bg;
  const resolvedFg = fg.a < 1 ? blendOverBackground(fg, resolvedBg) : fg;

  const fgLum = relativeLuminance(resolvedFg);
  const bgLum = relativeLuminance(resolvedBg);
  const lighter = Math.max(fgLum, bgLum);
  const darker = Math.min(fgLum, bgLum);
  return (lighter + 0.05) / (darker + 0.05);
}

function rgbToHex({ r, g, b }: Rgb): string {
  const toHex = (value: number) => clampByte(value).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function rgbToHsl({ r, g, b }: Rgb): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case rn:
        h = ((gn - bn) / delta + (gn < bn ? 6 : 0)) * 60;
        break;
      case gn:
        h = ((bn - rn) / delta + 2) * 60;
        break;
      default:
        h = ((rn - gn) / delta + 4) * 60;
        break;
    }
  }

  return { h, s, l };
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let rp = 0;
  let gp = 0;
  let bp = 0;

  if (h < 60) {
    rp = c;
    gp = x;
  } else if (h < 120) {
    rp = x;
    gp = c;
  } else if (h < 180) {
    gp = c;
    bp = x;
  } else if (h < 240) {
    gp = x;
    bp = c;
  } else if (h < 300) {
    rp = x;
    bp = c;
  } else {
    rp = c;
    bp = x;
  }

  return {
    r: clampByte((rp + m) * 255),
    g: clampByte((gp + m) * 255),
    b: clampByte((bp + m) * 255),
    a: 1,
  };
}

function minAccentSurfaceContrast(accent: string): number {
  return Math.min(
    ...BUSINESS_PLANE_CONTRAST_SURFACES.map((surface) => contrastRatio(accent, surface))
  );
}

/** §12-A — primary button text on accent; never hard-code #fff */
export function computeAccentOn(
  accent: string,
  _businessSurfaces: readonly string[] = BUSINESS_PLANE_CONTRAST_SURFACES
): string {
  let best: string = ACCENT_ON_CANDIDATES[0];
  let bestRatio = 0;

  for (const candidate of ACCENT_ON_CANDIDATES) {
    const ratio = contrastRatio(candidate, accent);
    if (ratio > bestRatio) {
      bestRatio = ratio;
      best = candidate;
    }
  }

  return best;
}

/** §12-B — clamp accent until WCAG AA vs Business dark surfaces */
export function clampAccentForBusinessPlane(accent: string): {
  accent: string;
  wasClamped: boolean;
} {
  const parsed = parseCssColor(accent);
  if (!parsed) {
    return { accent, wasClamped: false };
  }

  if (minAccentSurfaceContrast(accent) >= WCAG_AA_CONTRAST) {
    return { accent, wasClamped: false };
  }

  const { h, s, l } = rgbToHsl(parsed);
  let low = 0;
  let high = 1;
  let best = accent;
  let wasClamped = false;

  for (let step = 0; step < 24; step += 1) {
    const mid = (low + high) / 2;
    const candidate = rgbToHex(hslToRgb(h, s, mid));
    const ratio = minAccentSurfaceContrast(candidate);
    if (ratio >= WCAG_AA_CONTRAST) {
      best = candidate;
      wasClamped = true;
      high = mid;
    } else {
      low = mid;
    }
  }

  if (!wasClamped) {
    const lightCandidate = rgbToHex(hslToRgb(h, s, Math.min(l + 0.35, 1)));
    if (minAccentSurfaceContrast(lightCandidate) >= WCAG_AA_CONTRAST) {
      return { accent: lightCandidate, wasClamped: true };
    }
  }

  return { accent: best, wasClamped };
}

function resolveRawAccent(theme: TenantSiteThemeSlice): string | undefined {
  const cssVars = theme.cssVars ?? {};
  const primitive = theme.designManifest?.tokens.primitive ?? {};
  return (
    cssVars['--color-brand-primary'] ??
    primitive['--color-brand-primary'] ??
    theme.colors.primary ??
    theme.colors.accent
  );
}

function resolveInjectionLevel(theme: TenantSiteThemeSlice): 'L1' | 'L2' {
  return theme.businessBrandInjection === 'L2' ? 'L2' : 'L1';
}

function resolveBodyFont(theme: TenantSiteThemeSlice, level: 'L1' | 'L2'): string | undefined {
  if (level !== 'L2') {
    return undefined;
  }
  return theme.fonts.body ?? theme.cssVars?.['--font-body'] ?? undefined;
}

function parseRadiusPx(theme: TenantSiteThemeSlice, level: 'L1' | 'L2'): number | undefined {
  if (level !== 'L2') {
    return undefined;
  }
  const raw =
    theme.cssVars?.['--radius-md'] ??
    theme.cssVars?.['--radius-lg'] ??
    theme.designManifest?.tokens.component?.['--radius-md'];
  if (!raw) {
    return undefined;
  }
  const parsed = Number.parseFloat(raw.replace(/px$/, '').trim());
  if (!Number.isFinite(parsed)) {
    return undefined;
  }
  return Math.min(L2_RADIUS_MAX, Math.max(L2_RADIUS_MIN, parsed));
}

const BRAND_LOGO_CSS_KEYS = ['--brand-logo-url', '--logo-url'] as const;
const BRAND_TENANT_NAME_CSS_KEYS = ['--brand-tenant-name', '--tenant-display-name'] as const;

function pickCssVar(cssVars: Record<string, string>, keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const value = cssVars[key]?.trim();
    if (value) {
      return value;
    }
  }
  return undefined;
}

/** L1 — logo URL from published theme cssVars only (no Site :root bulk inject) */
export function resolveBrandLogoUrlFromTheme(theme: TenantSiteThemeSlice): string | undefined {
  return pickCssVar(theme.cssVars ?? {}, BRAND_LOGO_CSS_KEYS);
}

/** L1 — tenant display name from theme cssVars */
export function resolveBrandTenantDisplayNameFromTheme(
  theme: TenantSiteThemeSlice
): string | undefined {
  return pickCssVar(theme.cssVars ?? {}, BRAND_TENANT_NAME_CSS_KEYS);
}

function buildBrandIdentity(
  accentSource: string | undefined,
  theme: TenantSiteThemeSlice,
  extras?: { logoUrl?: string; tenantDisplayName?: string }
): BrandIdentity {
  const injectionLevel = resolveInjectionLevel(theme);
  const rawAccent = accentSource?.trim() || '#5eead4';
  const { accent, wasClamped } = clampAccentForBusinessPlane(rawAccent);
  const fontBody = resolveBodyFont(theme, injectionLevel);
  const radiusPx = parseRadiusPx(theme, injectionLevel);

  return {
    accent,
    accentOn: computeAccentOn(accent),
    injectionLevel,
    accentWasClamped: wasClamped,
    ...(extras?.logoUrl !== undefined ? { logoUrl: extras.logoUrl } : {}),
    ...(extras?.tenantDisplayName !== undefined
      ? { tenantDisplayName: extras.tenantDisplayName }
      : {}),
    ...(fontBody !== undefined ? { fontBody } : {}),
    ...(radiusPx !== undefined ? { radiusPx } : {}),
  };
}

/** Reduce published theme slice → BrandIdentity (no surface/spacing/display font) */
export function reduceBrandIdentityFromTheme(theme: TenantSiteThemeSlice): BrandIdentity {
  const logoUrl = resolveBrandLogoUrlFromTheme(theme);
  const tenantDisplayName = resolveBrandTenantDisplayNameFromTheme(theme);
  return buildBrandIdentity(resolveRawAccent(theme), theme, {
    ...(logoUrl !== undefined ? { logoUrl } : {}),
    ...(tenantDisplayName !== undefined ? { tenantDisplayName } : {}),
  });
}

/** Published model reduce — seo/jsonLd fallback for tenantDisplayName (§12-C) */
export function reduceBrandIdentityFromPublishedModel(model: TenantSiteModel): BrandIdentity {
  const theme = model.theme;
  const jsonLdName =
    typeof model.seo?.jsonLd?.name === 'string' ? model.seo.jsonLd.name.trim() : undefined;
  const tenantDisplayName =
    resolveBrandTenantDisplayNameFromTheme(theme) ??
    jsonLdName ??
    resolveSiteBrandLabel(model, model.tenantId);

  const publishedLogoUrl = resolveBrandLogoUrlFromTheme(theme);
  return buildBrandIdentity(resolveRawAccent(theme), theme, {
    ...(publishedLogoUrl !== undefined ? { logoUrl: publishedLogoUrl } : {}),
    tenantDisplayName,
  });
}

export interface ReduceBrandIdentityFromDesignOptions {
  /** Studio L2 opt-in — defaults L1 when omitted */
  businessBrandInjection?: 'L1' | 'L2';
}

/** Reduce SiteDesignManifest → BrandIdentity (Studio preview / tests) */
export function reduceBrandIdentityFromDesign(
  manifest: SiteDesignManifest,
  options?: ReduceBrandIdentityFromDesignOptions
): BrandIdentity {
  const primitive = manifest.tokens.primitive;
  const accent =
    primitive['--color-brand-primary'] ?? manifest.tokens.semantic['--color-brand-primary'];
  const fonts = themeFontsFromManifest(manifest);
  const component = manifest.tokens.component ?? {};
  const themeSlice: TenantSiteThemeSlice = {
    colors: { primary: accent ?? '#5eead4' },
    fonts,
    designManifest: manifest,
    cssVars: {
      '--color-brand-primary': accent ?? '#5eead4',
      ...(fonts.body !== undefined ? { '--font-body': fonts.body } : {}),
      ...(component['--radius-md'] ? { '--radius-md': component['--radius-md'] } : {}),
    },
    ...(options?.businessBrandInjection
      ? { businessBrandInjection: options.businessBrandInjection }
      : {}),
  };
  return buildBrandIdentity(accent, themeSlice);
}

export { contrastRatio, WCAG_AA_CONTRAST };
